import type { CityDto } from '@upme/contracts';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@upme/ui/components/alert-dialog';
import { Button } from '@upme/ui/components/button';
import { Input } from '@upme/ui/components/input';
import { useState } from 'react';
import { toast } from 'sonner';
import { useDeleteCity } from '../mutations';

interface Props {
  mode: 'soft' | 'hard';
  city: CityDto;
  onDeleted: () => void;
}

export function DeleteCityDialog({ mode, city, onDeleted }: Props) {
  const [confirm, setConfirm] = useState('');
  const del = useDeleteCity();

  const canConfirm = mode === 'soft' || confirm === city.slug;

  const handleConfirm = async () => {
    try {
      await del.mutateAsync({ id: city.id, hard: mode === 'hard' });
      toast.success(mode === 'soft' ? 'Город скрыт' : 'Город удалён');
      onDeleted();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Не удалось удалить';
      toast.error(message);
    }
  };

  if (mode === 'soft') {
    return (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="outline">Скрыть город</Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Скрыть город «{city.name}»?</AlertDialogTitle>
            <AlertDialogDescription>
              Он не будет показан публично, но сохранится в базе. Можно восстановить через переключатель
              «Активен».
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm}>Скрыть</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="destructive">Удалить навсегда</Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Удалить город «{city.name}» безвозвратно?</AlertDialogTitle>
          <AlertDialogDescription>
            Это действие нельзя отменить. Если у города есть связанные локации или мероприятия — удаление
            будет отклонено сервером.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="my-4">
          <p className="text-sm mb-2">
            Для подтверждения введите <code className="bg-muted px-1 rounded">{city.slug}</code>:
          </p>
          <Input placeholder="Введите slug" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Отмена</AlertDialogCancel>
          <AlertDialogAction disabled={!canConfirm} onClick={handleConfirm}>
            Удалить навсегда
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
